import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ClientConfig } from '../interfaces/client-config';

/**
 * Single source of truth for all environment-dependent configuration
 * (API URLs, feature flags, third-party integration keys).
 *
 * Loads configuration lazily from a static JSON asset so that the same
 * build artifact can be deployed to dev/staging/production by swapping
 * only the config file. The config is cached after first load to avoid
 * repeated HTTP requests -- every accessor returns an Observable to
 * keep callers resilient to the initial async load.
 *
 * Feature flag accessors (e.g. ircEnabled, protocolManagementEnabled)
 * are centralised here so that feature toggles are never scattered
 * across individual components.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientConfigService {
  private readonly httpClient = inject(HttpClient);

  private _config: ClientConfig;

  get config(): Observable<ClientConfig> {
    return this._config ? of(this._config) : this.load();
  }

  public apiUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.serverUrl));
  }

  public casUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.casUrl));
  }

  public casLogoutUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.casLogoutUrl));
  }

  public stateMonitorLogoutUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.stateMonitorLogoutUrl));
  }

  public ssoUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.ssoUrl));
  }
  public infoUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.infoUrl));
  }
  public supportUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.supportUrl));
  }
  public quickStartUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.quickStartUrl));
  }
  public userManualUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.userManualUrl));
  }

  public emailChangeUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.emailChangeUrl));
  }

  public myQiagenUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.myQiagenUrl));
  }

  public stateMonitorURL(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.stateMonitorURL));
  }

  public insightsURL(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.insightsURL));
  }

  public serviceTeamMail(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.serviceTeamMail));
  }
  public env(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.env));
  }

  public instrumentationKey(): Observable<string> {
    return this.config.pipe(map(e => e.ai?.instrumentationKey));
  }

  public hotjar(): Observable<{ enabled: boolean; id: number; sv: number }> {
    return this.config.pipe(map(e => e.hotjar));
  }

  public ircEnabled(): Observable<boolean> {
    return this.config.pipe(map(e => e.qiasphere.feature.instrumentRemoteConfig));
  }

  public serialNumberGenerationEnabled(): Observable<boolean> {
    return this.config.pipe(map(e => e.qiasphere.feature.serialNumberGeneration));
  }

  public protocolManagementEnabled(): Observable<boolean> {
    return this.config.pipe(map(e => e.qiasphere.feature.protocolManagement));
  }
  public organizationDataExportEnabled(): Observable<boolean> {
    return this.config.pipe(map(e => e.qiasphere.feature.organizationDataExport));
  }

  public releaseHighlightsUrl(): Observable<string> {
    return this.config.pipe(map(e => e.qiasphere.releaseHighlightsUrl));
  }

  load(): Observable<ClientConfig> {
    return this.httpClient.get<ClientConfig>('assets/client-config.json').pipe(
      map((data: ClientConfig) => {
        this._config = data;
        return data;
      })
    );
  }
}
